/**
 * How bad each issue is, for the Live Activity tabs (Memory Issues, DB Health Check, Code Review).
 * This mirrors backend/api/issue_impact.py, which the morning email and the urgent alerts use:
 * keep the two in step, so the tabs and the emails always agree.
 *
 * Every issue carries an impact: the share, 0-100 %, of what it affects, in its tab's own terms
 * (requests, the plan's memory, users, patient data). Issues that pass or don't apply count 0; one
 * that couldn't be checked has no impact (null): it is listed but left out of the numbers.
 *
 * An issue's effect says when that share is affected: "now" (it's affecting things right now: a
 * database service down; memory a leak or a heavy import holds all the time), "risk" (it would if it
 * happened: a security hole, a bug in a rare path, a warning) or "while_running" (memory one action
 * uses only while it runs).
 *
 * A tab's health is what's actually affected right now, so a working app with risks to fix doesn't
 * read as broken. DB Health and Code Review: 100 minus the biggest share affected now. Memory Issues:
 * 100 minus the share of the plan the open issues hold now, all together (the same plan, so they add
 * up), never more than the server actually uses; within that, the idle floor never more than the
 * server used when it started, and slow climbers never more than it has grown since.
 *
 * The state: urgent (needs immediate attention) when a quarter or more is affected now, a risk of a
 * quarter or more (an exposure of patient data), or, for memory, the server's current use plus one
 * action could push it to 90% of its limit; minor for anything of a few percent or more; good for
 * the rest.
 */
import { STATUS_COLORS, MEDIUM_COLOR } from "./statusColors";

export const URGENT_ISSUE = 25;      // this much affected now, or at risk, needs someone now
export const MINOR_ISSUE = 2;        // an issue this big is a minor issue; below it, good
export const CRASH_AT = 90;          // memory: use plus one action this close to the limit could crash the server
export const NOW = "now";
export const RISK = "risk";
export const WHILE_RUNNING = "while_running";
// Code checks whose findings mean code that can't run at all (not a risk: broken now).
const BROKEN_NOW_CHECKS = ["merge_conflicts", "python_syntax"];
export const STATE_WORDS = { good: "good", minor: "minor issues", urgent: "needs immediate attention" };
// The Chakra colour scheme each state is shown in.
export const STATE_SCHEME = { good: "green", minor: "orange", urgent: "red" };

function clamp(value) {
  if (value === null || value === undefined || value === "" || typeof value === "boolean") return null;
  const number = Number(value);
  if (Number.isNaN(number)) return null;
  return Math.max(0, Math.min(100, number));
}

function rounded(value, places) {
  const factor = 10 ** places;
  return Math.round(value * factor) / factor;
}

/** One issue's impact: {pct, of, basis, estimated}. A pct that isn't a number counts 0. */
export function impact(pct, of, basis = "", estimated = true) {
  return { pct: rounded(clamp(pct) ?? 0, 2), of, basis, estimated: Boolean(estimated) };
}

/** The impact, saying when it applies (NOW, RISK or WHILE_RUNNING) and, for memory, its layer. */
export function withEffect(value, effect, layer = null) {
  if (!value || typeof value !== "object") return value;
  return layer ? { ...value, effect, layer } : { ...value, effect };
}

/** Memory held all the time (the idle floor, slow climbers) is wasted now; a spike or a rare admin
 * action only uses its memory while it runs. */
export function memoryEffect(layer) {
  return layer === "floor" || layer === "climber" ? NOW : WHILE_RUNNING;
}

/** A DB check's problem is something failing now; a warning is one to keep an eye on. */
export function dbCheckEffect(status) {
  return status === "problem" ? NOW : RISK;
}

/** Code that can't run at all is broken now; every other code finding is a risk. */
export function codeCheckEffect(checkId, status) {
  return BROKEN_NOW_CHECKS.includes(checkId) && status === "problem" ? NOW : RISK;
}

/** An AI review finding: memory by its layer; anything else is a risk. */
export function findingEffect(finding) {
  return finding?.area === "memory" ? memoryEffect(finding.kind) : RISK;
}

/** When an issue's impact applies; one that doesn't say is a risk. */
export function effectOf(item) {
  const value = item && typeof item === "object" && "impact" in item ? item.impact : item;
  return (value && typeof value === "object" && value.effect) || RISK;
}

/** The impact of something found before impact was estimated: not sized yet (the next nightly run
 * sizes it), so it's listed but left out of the numbers, like one that couldn't be checked. Counting
 * each as a guess turned dozens of old findings into half of a tab's chart. */
export function notEstimated(of) {
  return { pct: null, of, basis: "Found before impact was estimated: the next nightly run sizes it.",
           estimated: true, not_estimated: true };
}

/** Whether an item is one of those: listed, not sized yet. */
export function notSized(item) {
  const value = item && typeof item === "object" && "impact" in item ? item.impact : item;
  return Boolean(value && typeof value === "object" && value.not_estimated && clamp(value.pct) === null);
}

/** The impact of an issue (an object with `impact`), of an impact object, or a bare number; null if unknown. */
export function pctOf(item) {
  let value = item;
  if (value && typeof value === "object" && "impact" in value) value = value.impact;
  if (value && typeof value === "object") value = value.pct;
  return value === null || value === undefined ? null : clamp(value);
}

function knownEffects(items) {
  return (items || []).map((i) => [pctOf(i), effectOf(i)]).filter(([pct]) => pct !== null);
}

function isSet(value) {
  return value !== null && value !== undefined;
}

/** The share of the plan the open memory issues hold right now: the idle floor's, never more than the
 * server used when it started (startPct), plus the slow climbers', never more than it has grown since
 * (usedPct - startPct), and never more than it uses (usedPct). Like held_now() in Python. */
export function heldNow(items, { usedPct = null, startPct = null } = {}) {
  let floor = 0;
  let climber = 0;
  (items || []).forEach((item) => {
    const pct = pctOf(item);
    if (pct === null || effectOf(item) !== NOW) return;
    const value = item && typeof item === "object" && "impact" in item ? item.impact : item;
    if (value?.layer === "floor") floor += pct; else climber += pct;
  });
  if (isSet(startPct)) {
    floor = Math.min(floor, Math.max(0, startPct));
    if (isSet(usedPct)) climber = Math.min(climber, Math.max(0, usedPct - startPct));
  }
  const held = Math.min(100, floor + climber);
  return rounded(isSet(usedPct) ? Math.min(held, Math.max(0, usedPct)) : held, 2);
}

/** What isn't affected right now, 0-100, one decimal: 100 minus the biggest share affected now; for
 * memory ({ memory: true }), minus what the issues hold now (heldNow). */
export function healthOf(items, { memory = false, usedPct = null, startPct = null } = {}) {
  if (memory) return rounded(100 - heldNow(items, { usedPct, startPct }), 1);
  const now = knownEffects(items).filter(([, effect]) => effect === NOW).map(([pct]) => pct);
  return rounded(100 - now.reduce((worst, pct) => Math.max(worst, pct), 0), 1);
}

/** "urgent", "minor" or "good" (see the top of this file). */
export function stateOf(items, { memory = false, usedPct = null, startPct = null } = {}) {
  const known = knownEffects(items);
  const left = healthOf(items, { memory, usedPct, startPct });
  if (100 - left >= URGENT_ISSUE) return "urgent";                  // a quarter or more affected right now
  if (memory) {
    const biggestRun = known.filter(([, effect]) => effect !== NOW).reduce((max, [pct]) => Math.max(max, pct), 0);
    const inUse = usedPct !== null && usedPct !== undefined ? usedPct : 100 - left;
    if (biggestRun && inUse + biggestRun >= CRASH_AT) return "urgent";
  } else if (known.some(([pct, effect]) => effect === RISK && pct >= URGENT_ISSUE)) {
    return "urgent";
  }
  if (known.some(([pct]) => pct >= MINOR_ISSUE)) return "minor";
  return "good";
}

/** A tab's numbers from its issues: {health, state, words, top, issues, unknown, not_estimated,
 * serious_risks}, like summarize() in Python. */
export function summarizeImpact(items, top = 5, options = {}) {
  const all = items || [];
  const knownItems = all.filter((i) => pctOf(i) !== null);
  const open = knownItems.filter((i) => pctOf(i) > 0).sort((a, b) => pctOf(b) - pctOf(a));
  const state = stateOf(knownItems, options);
  const unsized = all.filter(notSized).length;
  const serious = options.memory ? 0 : open.filter((i) => effectOf(i) === RISK && pctOf(i) >= URGENT_ISSUE).length;
  return { health: healthOf(knownItems, options), state, words: STATE_WORDS[state], top: open.slice(0, top),
           issues: open.length, unknown: all.length - knownItems.length - unsized, not_estimated: unsized,
           serious_risks: serious };
}

function number(pct) {
  if (pct >= 10 || pct === Math.trunc(pct)) return pct.toFixed(0);
  return pct.toFixed(1).replace(/0+$/, "").replace(/\.$/, "");
}

const WHEN = { [NOW]: " now", [RISK]: " at risk", [WHILE_RUNNING]: " while it runs" };

/** The impact in a few words, and when: "≈30% of requests now", "<2% of users at risk", "≈6% of the 2 GB
 * plan while it runs". */
export function chipText(value) {
  if (!value) return "impact not known";
  if (value.not_estimated) {
    const counted = clamp(value.pct);    // a check from a report saved before checks had a size
    return counted ? `impact not estimated, counted as ${number(counted)}%` : "impact not estimated";
  }
  const pct = clamp(value.pct);
  const of = value.of || "the whole";
  if (!pct) return "no impact";
  let size;
  if (pct < 1) size = `under 1% of ${of}`;
  else if (pct < MINOR_ISSUE) size = `<2% of ${of}`;
  else size = `${value.estimated === false ? "" : "≈"}${number(pct)}% of ${of}`;
  return size + (WHEN[value.effect] || "");
}

/** An AI finding's impact, from the impact_pct / impact_of the review gave it (not sized yet when it gave none). */
export function findingImpact(finding) {
  const of = finding?.impact_of || "users";
  const pct = clamp(finding?.impact_pct);
  if (pct === null) return notEstimated(of);
  return impact(pct, of, "Estimated by the nightly AI review.");
}

/** A DB Health or Code Review check's impact. A report saved before checks had one: a problem counts
 * as an urgent-sized issue and a warning as a minor one, both marked not estimated. */
export function checkImpact(check, of) {
  if (check && Object.prototype.hasOwnProperty.call(check, "impact")) return check.impact ?? null;
  const status = check?.status;
  if (status === "ok") return impact(0, of);
  if (status === "problem" || status === "warning") {
    const pct = status === "problem" ? URGENT_ISSUE : MINOR_ISSUE;
    return { ...impact(pct, of, `Checked before impact was estimated, so a ${status} counts as ${pct}%.`),
             not_estimated: true };
  }
  return null;
}

/** A tab's items split for its two rings: health (what's broken now, what passed, and what couldn't be
 * checked) and risk (what could go wrong, sized or not yet). */
export function splitHealthAndRisk(items) {
  const health = [];
  const risk = [];
  (items || []).forEach((item) => {
    const pct = pctOf(item);
    const forHealth = effectOf(item) === NOW || pct === 0 || (pct === null && !notSized(item));
    (forHealth ? health : risk).push(item);
  });
  return { health, risk };
}

/** What a memory issue is a share of: "the 2 GB plan". */
export function planLabel(limitMb) {
  const mb = Number(limitMb) > 0 ? Number(limitMb) : 2048;
  const size = mb >= 1024 ? `${Number((mb / 1024).toFixed(1))} GB` : `${Math.round(mb)} MB`;
  return `the ${size} plan`;
}

/** Sort key: open issues by size first, then ones that couldn't be checked, then the ones that pass. */
export function rankOfPct(pct) {
  if (pct === null || pct === undefined) return -0.5;
  return pct > 0 ? pct : -1;
}

/** items sorted most important first: open ones by impact, largest first, then the ones not sized or
 * checked yet, then the ones that pass. Between two the same size, the higher worseFor(item) comes
 * first (a problem before a warning); otherwise they keep their order. */
export function byImpact(items, pctFor = pctOf, worseFor = () => 0) {
  return (items || [])
    .map((item, index) => ({ item, index, rank: rankOfPct(pctFor(item)), worse: worseFor(item) || 0 }))
    .sort((a, b) => b.rank - a.rank || b.worse - a.worse || a.index - b.index)
    .map((x) => x.item);
}

/** How bad a status is, for byImpact's tie-break: a problem (or a failed check) before a warning. */
export function worseOf(status) {
  return { problem: 2, fail: 2, error: 1, warning: 1 }[status] || 0;
}

/** The colour of one issue (or a group of them) by its own size. */
export function impactColor(pct) {
  if (pct >= URGENT_ISSUE) return STATUS_COLORS.critical;
  if (pct >= MINOR_ISSUE) return MEDIUM_COLOR;
  return STATUS_COLORS.warning;
}

/** The donut's slices: the green health and, as one arc, what's affected right now (coloured by its
 * size), with the `top` biggest open issues (now or at risk) named for the legend and the rest grouped
 * as "N smaller issues". Only the health and that arc are drawn (`value`); every slice keeps its own
 * `pct` for the legend. Items are {key, label, impact}. */
export function impactSlices(items, { top = 5, memory = false, usedPct = null, startPct = null } = {}) {
  const open = (items || [])
    .map((item, index) => ({ item, index, pct: pctOf(item) }))
    .filter((x) => x.pct !== null && x.pct > 0)
    .sort((a, b) => b.pct - a.pct || a.index - b.index);
  const health = healthOf(items, { memory, usedPct, startPct });
  const slices = open.slice(0, top).map(({ item, pct }, i) => ({
    key: item.key ?? item.id ?? `issue-${i}`,
    label: item.label ?? item.title ?? item.key ?? item.id ?? "issue",
    pct,
    value: 0,
    color: impactColor(pct),
    impact: item.impact ?? null,
  }));
  const rest = open.slice(top);
  if (rest.length) {
    // Each of them is no bigger than the biggest of them: its size, and its colour (a pile of small
    // issues is yellow or orange, never red).
    slices.push({ key: "rest", label: `${rest.length} smaller ${rest.length === 1 ? "issue" : "issues"}`,
                  pct: rest[0].pct, value: 0, color: impactColor(rest[0].pct), count: rest.length });
  }
  if (health < 100) {
    slices.push({ key: "now", label: "affected now", pct: 100 - health, value: 100 - health,
                  color: impactColor(100 - health) });
  }
  if (health > 0) slices.push({ key: "healthy", label: "healthy", pct: health, value: health, color: STATUS_COLORS.good });
  return slices;
}
